<script>
import { mapActions, mapMutations, mapState, mapGetters } from "vuex";
import FooterWrapper from "@/components/FooterWrapper";

export default {
  // Included components
  components: {
    FooterWrapper,
  },

  data() {
    return {
      // The new user's email
      email: "",

      // The new user's password
      password: "",

      // The confirmation of the password
      confirmPassword: "",

      // The new user's handle
      handle: "",

      // The user's name
      name: "",

      // Whether the user has accepted ToS and PP
      acceptedTerms: false,

      // Whether marketing emails are enabled
      marketingEmailsEnabled: true,

      // An optional invitation ID
      invitationId: null,

      // The actual (optional) invitation
      invitation: null,

      // Whether the invitation ID was bad
      badInvitation: false,

      // Whether an invitation is being loaded
      loadingInvitation: true,

      // The next path to go to after signing up/in
      next: null,
    };
  },

  watch: {
    // When the password or confirmation changes, validate that they are the same
    password() {
      this.validate();
    },

    // When the password or confirmation changes, validate that they are the same
    confirmPassword() {
      this.validate();
    },

    // Check that the handle is lowercase and validate accordingly
    handle() {
      this.validate();
    },
  },

  computed: {
    // Mapped vuex state
    ...mapState(["teams", "activeTeamId", "permissions", "teamContracts"]),
    ...mapGetters(["authenticated", "can"]),
  },

  // When the page is created, grab the invitation ID
  // from the URL, and (if present), fetch the invitation
  async created() {
    this.next = this.$route.query.next;
    this.invitationId = this.$route.query.invitationId;
    this.email = this.$route.query.email || "";
    if (this.invitationId != null) {
      let invitation = await this.getInvitation({
        invitationId: this.invitationId,
      });
      if (invitation) {
        this.invitation = invitation;
      } else {
        this.badInvitation = true;
      }
    } else {
      // redirect to login if there's no invitation
      this.$router.push("/sign-in");
    }
    this.loadingInvitation = false;
  },

  methods: {
    // Validate that the two password fields match
    validate() {
      if (
        this.password &&
        this.confirmPassword &&
        this.password != this.confirmPassword
      ) {
        this.$refs.confirmPasswordInput.setCustomValidity(
          "Passwords do not match"
        );
      } else {
        this.$refs.confirmPasswordInput.setCustomValidity("");
      }
      if (this.handle !== this.handle.toLowerCase()) {
        this.$refs.handleInput.setCustomValidity(
          "User handle must be lowercase"
        );
      } else {
        this.$refs.handleInput.setCustomValidity("");
      }
    },

    // Submit sign up creds from form
    async submit() {
      // Sign up given the form credentials
      const result = await this.signUp({
        email: this.email,
        password: this.password,
        handle: this.handle,
        name: this.name,
        marketingEmailsEnabled: this.marketingEmailsEnabled,
        invitationId: this.invitationId,
      });
      console.log("RESPONSE", result);

      // If they successfully register, sign them in as well
      // Users now are logged in after signing up
      if (result.ok) {
        await Promise.all([
          this.getUser(),
          this.getTeams(),
          this.getPermissions(),
          this.getAccountRoles(),
        ]);

        // Get the first team to set active
        const firstTeam = Object.keys(this.teams)[0];

        // Set first team to active
        await this.setActiveTeamId(firstTeam);
        // Retrieve the Contracts for the Active Team ID
        // This API Call returns all contracts for the team in reverse order of creation
        // Perform the contract completion checks and route immediately on sign up so as to not render
        // Overview briefly then be rerouted
        await this.getTeamContracts();
        if (this.teamContracts) {
          if (this.teamContracts.length > 0) {
            let most_recent_contract = this.teamContracts[0];
            if (this.can.billing.manage) {
              if (!most_recent_contract.payment_method_ingested) {
                await this.$router.push("/ingest-payment-method");
              } else if (!most_recent_contract.onboarding_invoice_created) {
                await this.$router.push("/confirm-charge");
              } else {
                await this.$router.push({ path: "/" });
              }
            }
          }
        }
        await this.$router.push({ path: "/" });
      }
    },

    // Mapped vuex actions
    ...mapActions([
      "signUp",
      "getInvitation",
      "getUser",
      "getTeams",
      "getAccountRoles",
      "getPermissions",
      "getTeamContracts",
    ]),
    ...mapMutations(["setActiveTeamId", "pushToast"]),
  },
};
</script>

<template>
  <FooterWrapper>
    <b-container class="mt-3" v-if="!loadingInvitation">
      <b-row align-h="center" v-if="badInvitation">
        <b-col lg="9" xl="8">
          <b-row class="mb-3">
            <b-col>
              <h1>Invalid Invitation</h1>
            </b-col>
          </b-row>

          <b-card class="text-center py-5">
            <b-card-title>Invalid Invitation</b-card-title>
            <b-card-text>
              Please check the link you received in the invitation email
            </b-card-text>
          </b-card>
        </b-col>
      </b-row>
      <b-row align-h="center" v-else>
        <b-col lg="9" xl="8">
          <b-row class="mb-3">
            <b-col>
              <h1 v-if="invitation">
                Join the {{ invitation.team.name }} Team
              </h1>
              <h1 v-else>Sign Up</h1>
            </b-col>
          </b-row>

          <b-card>
            <b-card-text>
              <b-form @submit.stop.prevent="submit">
                <b-form-group id="name-group" label="Name" label-for="name">
                  <b-form-input
                    id="name"
                    v-model="name"
                    placeholder="Name"
                  ></b-form-input>
                  <small class="form-text text-muted">
                    Your name will be displayed to members of teams that you
                    join.
                  </small>
                </b-form-group>
                <b-form-group
                  id="email-group"
                  label="Email Address"
                  label-for="email"
                >
                  <b-form-input
                    required
                    id="email"
                    type="email"
                    v-model="email"
                    placeholder="Email"
                  ></b-form-input>
                  <small class="form-text text-muted">
                    Your email will be used for app-related notifications.
                  </small>
                </b-form-group>
                <b-form-group
                  id="handle-group"
                  label="User Handle"
                  label-for="handle"
                >
                  <input
                    required
                    ref="handleInput"
                    id="handle"
                    :value="handle.toLowerCase()"
                    @input="handle = $event.target.value.toLowerCase()"
                    placeholder="Handle"
                    autocomplete="off"
                    class="form-control"
                    minlength="3"
                    maxlength="64"
                  />
                  <small class="form-text text-muted">
                    Your handle will act as a public username and
                    <b>
                      must not be the same as the team handle or contain any
                      symbols.
                    </b>
                    The handle
                    <b>will always be lowercase</b>
                    .
                  </small>
                </b-form-group>
                <b-row>
                  <b-col>
                    <b-form-group
                      id="password-group"
                      label="Password"
                      label-for="password"
                    >
                      <b-form-input
                        required
                        id="password"
                        type="password"
                        v-model="password"
                        placeholder="Password"
                        minlength="8"
                      ></b-form-input>
                      <small class="form-text text-muted">
                        Password must be at least 8 characters.
                      </small>
                    </b-form-group>
                  </b-col>
                  <b-col>
                    <b-form-group
                      id="confirm-password-group"
                      label="Confirm Password"
                      label-for="confirm-password"
                    >
                      <input
                        required
                        ref="confirmPasswordInput"
                        id="confirm-password"
                        type="password"
                        v-model="confirmPassword"
                        placeholder="Confirm Password"
                        class="form-control"
                      />
                    </b-form-group>
                  </b-col>
                </b-row>
                <b-form-checkbox
                  required
                  id="accepted-terms"
                  v-model="acceptedTerms"
                  name="accepted-terms"
                >
                  I accept the
                  <b-link>terms and conditions</b-link>
                  .
                </b-form-checkbox>
                <b-form-checkbox class="mb-3" v-model="marketingEmailsEnabled">
                  Opt in to emails about Navier and mine.app
                </b-form-checkbox>
                <b-button type="submit" variant="primary">Sign Up</b-button>
                <span v-if="invitation" class="pl-1">
                  or
                  <b-link
                    :to="{
                      path: '/sign-in',
                      query: { next: `/invitations/${invitationId}` },
                    }"
                  >
                    Log In
                  </b-link>
                  to accept this invitation
                </span>
              </b-form>
            </b-card-text>
          </b-card>
        </b-col>
      </b-row>
    </b-container>
  </FooterWrapper>
</template>

<style scoped>
.progress {
  height: 0.5rem;
}
</style>
